import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';

import Layout from '../../layout/Layout';
import Card from '../../components/Card/Card';
import ArticlesBanner from './components/ArticlesBanner';
import { articleStatus } from '../../constants/articleStatus';
import ArticlesStatus from './components/ArticlesStatus';
import ArticlesTheme from './components/ArticlesTheme';
import ArticlesCreateEditSubheader from './components/ArticlesCreateEditSubheader';
import ArticlesBrand from './components/ArticlesBrand';
import ArticlesGeneral from './components/ArticlesGeneral';
import ArticlesCars from './components/ArticlesCars';
import ArticlesCategory from './components/ArticlesCategory';
import useQueryParams from '../../hooks/useQueryParams';
import getArticlesDetails, { clearArticlesDetails } from '../../thunks/articles/getArticlesDetails';
import ArticlesDetailsSkeleton from './ArticlesDetailsSkeleton';
import ArticlesMarketplace from './components/ArticlesMarketplace';
import { articleMarketplaces } from '../../constants/articleMarketplaces';
import validationSchema from './validationSchema';
import prepareArticleRequest from '../../helpers/prepareArticleRequest';
import useUploadToS3 from '../../hooks/useUploadToS3';
import patchArticles, { clearArticlesUpdate } from '../../thunks/articles/patchArticles';
import ArticlesInformation from './components/ArticlesInformation';
import postArticles, { clearPostArticlesData } from '../../thunks/articles/postArticles';
import ArticlesSEO from './components/ArticlesSEO';
import ArticlesRelatedListings from './components/ArticlesRelatedListings';
import { resetListingsFilter, setListingsFilter } from '../../actions/listings/listingsFilter';

const containerClassName = 'flex flex-row';
const leftContainerClassName = 'flex flex-col flex-1 mr-[20px]';
const centerContainerClassName = 'flex flex-col flex-[2] mx-[20px] mb-[20px]';
const rightContainerClassName = 'flex flex-col flex-1 ml-[20px]';
const cardBodyClassName = 'px-[30px] pb-[30px]';
const cardContainerClassName = 'flex-none mb-[40px]';

const ArticlesCreateEdit = () => {
  const { saveFile, uploading: s3Uploading } = useUploadToS3();
  const [{ id }] = useQueryParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    articlesDetails,
    articlesDetailsLoading,
    patchArticles: {
      loading: patchLoading,
      data: patchData,
      error: patchError
    },
    postArticles: {
      loading: createLoading,
      data: createData,
      error: createError
    }
  } = useSelector((state) => ({
    articlesDetailsLoading: state?.getArticlesDetails?.loading,
    articlesDetails: state?.getArticlesDetails.data,
    patchArticles: state?.patchArticles,
    postArticles: state?.postArticles,
  }));

  const {
    values: {
      bannerImage,
      status,
      theme,
      brand,
      category,
      marketplace,
      relatedItem,
      articleCars
    },
    values: fromValues,
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    errors,
    touched,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: {
      title: '',
      metaTitle: '',
      url: '',
      shortDescription: '',
      metaDescription: '',
      content: '',
      bannerImage: '',
      status: { label: articleStatus.NEW, value: articleStatus.NEW },
      marketplace: { label: articleMarketplaces.TWO_DAY_PART, value: articleMarketplaces.TWO_DAY_PART },
      articleCars: [],
      theme: [],
      brand: [],
      category: null,
      relatedItem: null
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      let data = values;
      if (values?.bannerImageFile) {
        const { location: banner } = await saveFile(values?.bannerImageFile);
        data = { ...data, bannerImage: banner };
        delete data.bannerImageFile;
      }
      const formData = await prepareArticleRequest(data);
      if (id) {
        dispatch(patchArticles({
          id,
          ...formData
        }));
      } else {
        dispatch(postArticles({
          ...(values.status?.value === articleStatus.POSTED
              && ({ article_post_date: DateTime.local().setLocale('en').toFormat('yyyy-LL-dd HH:mm:ss') })),
          ...formData
        }));
      }
    },
    validationSchema
  });

  useEffect(() => {
    batch(() => {
      dispatch(clearArticlesUpdate());
      dispatch(resetListingsFilter());
    });
  }, []);

  useEffect(() => {
    dispatch(setListingsFilter(
      {
        filter: {
          ...(category
            && { listing_ebay_category: category }),
          ...(brand
            && {
              listing_manufacturer: brand?.map(({ value }) => value),
            }),
        },
      }
    ));
  }, [fromValues]);

  useEffect(() => {
    if (createError || patchError) {
      enqueueSnackbar(createError || patchError, { variant: 'error' });
      batch(() => {
        dispatch(clearArticlesUpdate());
        dispatch(clearPostArticlesData());
      });
    }
  }, [createError, patchError]);

  useEffect(() => {
    if (patchData || createData) {
      enqueueSnackbar(`${createData ? 'Create' : 'Update'} successfully`, { variant: 'success' });
      batch(() => {
        dispatch(clearArticlesUpdate());
        dispatch(clearPostArticlesData());
      });
    }
    if (createData) {
      const { id: newId } = createData?.data;
      navigate(`/articles/edit?id=${newId}`);
    }
  }, [patchData, createData]);

  useEffect(() => {
    if (id) {
      dispatch(getArticlesDetails(id));
    }
    return () => dispatch(clearArticlesDetails());
  }, [id]);

  useEffect(() => {
    if (articlesDetails) {
      setValues({
        ...fromValues,
        bannerImage: articlesDetails?.article_main_image,
        title: articlesDetails?.article_title,
        metaTitle: articlesDetails?.article_meta_title,
        url: articlesDetails?.article_slug,
        shortDescription: articlesDetails?.article_short_description,
        metaDescription: articlesDetails?.article_meta_description,
        ...articlesDetails?.article_status
        && ({ status: { label: articlesDetails?.article_status, value: articlesDetails?.article_status } }),
        content: articlesDetails?.article_html,
        brand: articlesDetails?.article_brand?.map((item) => ({ label: item, value: item })),
        theme: articlesDetails?.article_section?.map((item) => ({ label: item, value: item })),
        carYear: articlesDetails?.article_car_years?.map((item) => ({ label: item, value: item })),
        category: parseInt(articlesDetails?.article_categories?.id),
        relatedItem: articlesDetails?.article_related_items,
        articleCars: articlesDetails?.article_cars || []
      }, Boolean(id));
    }
  }, [articlesDetails]);

  const onSelect = (field, data) => setFieldValue(field, data);

  return (
    <Layout subheader={(
      <ArticlesCreateEditSubheader
        isValid={isValid && !patchLoading && !createLoading && !s3Uploading}
        onSave={handleSubmit}
        loading={patchLoading && createLoading && s3Uploading}
      />
    )}
    >
      {articlesDetailsLoading ? <ArticlesDetailsSkeleton />
        : (
          <div className={containerClassName}>
            <div className={leftContainerClassName}>
              <ArticlesBanner bannerImage={bannerImage} setFieldValue={setFieldValue} />
              <ArticlesSEO
                handleChange={handleChange}
                handleBlur={handleBlur}
                errors={errors}
                touched={touched}
                values={fromValues}
              />
              <ArticlesCars onSelect={onSelect} values={articleCars} />
            </div>
            <div className={centerContainerClassName}>
              <Card
                headerTitle="General"
                showHeader
                styleBodyContainer={cardBodyClassName}
                styleContainer={cardContainerClassName}
                headerBordered={false}
              >
                <ArticlesGeneral
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors}
                  touched={touched}
                  values={fromValues}
                  setFieldValue={setFieldValue}
                />
              </Card>
              <ArticlesRelatedListings
                relatedItem={relatedItem}
                setFieldValue={setFieldValue}
              />
            </div>
            <div className={rightContainerClassName}>
              {id && <ArticlesInformation />}
              <ArticlesMarketplace onSelect={(data) => onSelect('marketplace', data)} value={marketplace} />
              <ArticlesStatus onSelect={(data) => onSelect('status', data)} value={status} />
              <ArticlesBrand onSelect={(data) => onSelect('brand', data)} value={brand} />
              <ArticlesTheme onSelect={(data) => onSelect('theme', data)} value={theme} />
              <ArticlesCategory setFieldValue={setFieldValue} selectedCategory={category} />
            </div>
          </div>
        )}
    </Layout>
  );
};

export default ArticlesCreateEdit;
